import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import './Jornadas.css';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';
import { useCalendarioDetalle } from '../hooks/useCalendarioDetalle';
import { useFiltroJornadas } from '../hooks/useFiltroJornadas';
import { esPartidoDeJugador, calcularRankingAcumulado, partidoContieneTexto } from '../utils/jornadasUtils';
import PartidoJornada from './PartidoJornada';
import { usePistasPartidos } from '../hooks/usePistasPartidos';
import { useDatosJornadas } from '../hooks/useDatosJornadas';
import RankingAcumulado from './RankingAcumulado';

const MODALIDADES = [
    'DOBLES FEMENINO',
    'DOBLES MASCULINO',
    'DOBLES MIXTO',
    'INDIVIDUAL FEMENINO',
    'INDIVIDUAL MASCULINO',
];

const capitalize = (s) => s.charAt(0) + s.slice(1).toLowerCase();

const JornadaDetalle = () => {
    const { id } = useParams();
    const numJornada = Number(id);
    const { temporada } = useTemporada();
    const { usuario } = useAuth();
    const location = useLocation();
    const esPrivado = location.pathname.startsWith('/privado');
    const basePath = esPrivado ? '/privado/jornadas' : '/jornadas';

    const { partidos, rosterPorParticipante, cargando, error } = useCalendarioDetalle(temporada);
    const {
        soloMios, setSoloMios, puedeFiltrarMios, modalidad, setModalidad, busqueda, setBusqueda,
    } = useFiltroJornadas({ esPrivado, jugadorId: usuario?.jugadorId });

    const term = busqueda.trim();
    const jornadasPista = useMemo(() => [numJornada], [numJornada]);
    const pistasPorPartido = usePistasPartidos(temporada, jornadasPista, !esPrivado);
    const sede = useDatosJornadas(temporada)[numJornada]?.sede;

    const partidosDeLaJornada = partidos.filter((j) => j.jornada === numJornada);

    const partidosFiltrados = partidosDeLaJornada.filter((j) => {
        if (modalidad !== 'Todos' && j.modalidad !== modalidad) return false;
        if (soloMios) return esPartidoDeJugador(j, usuario.jugadorId, rosterPorParticipante);
        return partidoContieneTexto(j, term, rosterPorParticipante);
    });

    // El ranking acumulado siempre es global (todas las modalidades, todos
    // los jugadores): filtrar por modalidad o por "mis partidos" cambiaría
    // qué partidos se ven arriba, pero no debe vaciar ni sesgar la tabla.
    const { jornadas, filas } = calcularRankingAcumulado(partidos, numJornada);

    return (
        <div className="jornadas-page">
            <p className="jornadas-volver">
                <Link to={basePath}>← Volver a jornadas</Link>
            </p>
            <h2 className="jornadas-titulo">Jornada {numJornada} · Temporada {temporada}</h2>

            <div className="modalidad-filtro">
                <label>
                    <input type="radio" name="modalidad" value="Todos" onChange={() => setModalidad('Todos')} checked={modalidad === 'Todos'} />
                    Todas
                </label>
                {MODALIDADES.map(m => (
                    <label key={m}>
                        <input type="radio" name="modalidad" value={m} onChange={() => setModalidad(m)} checked={modalidad === m} />
                        {capitalize(m)}
                    </label>
                ))}
            </div>

            {puedeFiltrarMios && (
                <label className="jornadas-toggle-mios">
                    <input
                        type="checkbox"
                        checked={!soloMios}
                        onChange={(e) => setSoloMios(!e.target.checked)}
                    />
                    Ver todos los partidos
                </label>
            )}

            {!soloMios && (
                <div className="jornadas-toolbar">
                    <input
                        type="search"
                        placeholder="Buscar participante..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        className="jornadas-busqueda-input"
                    />
                </div>
            )}

            {cargando && <p className="jornadas-mensaje">Cargando...</p>}
            {error && <p className="jornadas-mensaje jornadas-error">{error}</p>}
            {!cargando && !error && partidosFiltrados.length === 0 && (
                <p className="jornadas-mensaje">
                    {soloMios ? 'No tienes partidos con este filtro en esta jornada.' : 'No hay partidos con este filtro en esta jornada.'}
                </p>
            )}

            <div className="ronda">
                {partidosFiltrados.map(partido => (
                    <PartidoJornada key={partido.id} partido={partido} pista={pistasPorPartido[partido.id]} sede={sede} />
                ))}
            </div>

            {!cargando && !error && (
                <>
                    <h3 className="jornadas-subtitulo">Ranking acumulado</h3>
                    <RankingAcumulado jornadas={jornadas} filas={filas} />
                </>
            )}
        </div>
    );
};

export default JornadaDetalle;
